import { Permission, SupportEmailAddress } from 'shared'
import { notificationHandler } from 'hooks/notificationHandler'
import {
  SharePointDrive,
  useConcreteTestIfiSettingsQuery,
  useControlPointIfiSettingsQuery,
  useDensityTestIfiSettingsQuery,
  useEditProjectMetadataMutation,
  useGetCmProjectLayersWgs84Query,
  useGetProjectSettingsProjectAdminsQuery,
  useInspectionIfiSettingsQuery,
  useLotIfiSettingsQuery,
  useMetadataQuery,
  usePunchListIfiSettingsQuery,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import { errorHandler } from 'hooks/errorHandler'
import { useGlobalState } from 'context/GlobalStateContext'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { useEffect, useState } from 'react'
import CIMSLink from 'components/CIMSLink'
import { useCheckPermission } from '../../../../../../../../hooks/useCheckPermission'
import { ProjectSettingsContext } from './types'

const { Provider, useContextFn } = createStrictContext<ProjectSettingsContext>(
  'ProjectSettingsContext'
)

export const useProjectSettingsContext = useContextFn

export const ProjectSettingsProvider: React.FC = ({ children }) => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const {
    data,
    refetch,
    loading: fetching,
  } = useMetadataQuery({
    ...DefaultQueryHookOptions,
    onError: (error) => {
      const { message } = error
      if (
        message.startsWith(
          'Could not find any entity of type "ProjectMetadata" matching'
        )
      ) {
        notificationHandler({
          title:
            "There doesn't appear to be any Metadata associated with this Project",
          alternateType: 'info',
          details: (
            <div>
              If you feel this is incorrect, please contact our{' '}
              <CIMSLink external href={`mailto:${SupportEmailAddress}`}>
                support
              </CIMSLink>{' '}
              team
            </div>
          ),
        })
      } else errorHandler(error)
    },
    variables: { projectId: projectId! },
    skip: !projectId,
  })
  const canEdit = useCheckPermission(Permission.EditProjectSettings)
  const [editMetadata] = useEditProjectMetadataMutation(
    DefaultMutationHookOptions
  )
  const projectAdminsQuery = useGetProjectSettingsProjectAdminsQuery({
    ...DefaultQueryHookOptions,
    variables: { projectId: projectId! },
    skip: !projectId,
  })

  const commonQueryOptions = {
    ...DefaultQueryHookOptions,
    variables: { projectId: projectId! },
    skip: !projectId,
  }
  const {
    data: concreteTestData,
    refetch: concreteTestRefetch,
    loading: concreteTestFetching,
  } = useConcreteTestIfiSettingsQuery(commonQueryOptions)
  const {
    data: punchListData,
    refetch: punchListRefetch,
    loading: punchListFetching,
  } = usePunchListIfiSettingsQuery(commonQueryOptions)
  const {
    data: inspectionData,
    refetch: inspectionRefetch,
    loading: inspectionFetching,
  } = useInspectionIfiSettingsQuery(commonQueryOptions)
  const {
    data: controlPointData,
    refetch: controlPointRefetch,
    loading: controlPointFetching,
  } = useControlPointIfiSettingsQuery(commonQueryOptions)
  const {
    data: densityTestData,
    refetch: densityTestRefetch,
    loading: densityTestFetching,
  } = useDensityTestIfiSettingsQuery(commonQueryOptions)
  const {
    data: lotData,
    refetch: lotRefetch,
    loading: lotFetching,
  } = useLotIfiSettingsQuery(commonQueryOptions)

  const { data: projectLayersData } = useGetCmProjectLayersWgs84Query({
    ...DefaultQueryHookOptions,
  })
  const projectLayer = projectLayersData?.cmProjectLayers?.find(
    ({ id }) => data?.project?.projectMetadata?.cmProjectLayersId === id
  )
  const projectLayerOptions = useOptions({
    items: projectLayersData?.cmProjectLayers,
    value: 'id',
    label: 'name',
    tooltip: ({ id }) => id.toString(),
    sort: 'name',
  })
  const sharePointDrives = useOptions({
    items: data?.project?.sharePointDrives,
    value: 'id',
    label: 'name',
    tooltip: 'description',
    sort: 'name',
  })
  const [sharePointDrive, setSharePointDrive] = useState<
    SharePointDrive | undefined
  >()
  useEffect(() => {
    if (!data?.project?.projectMetadata?.customDriveId)
      return setSharePointDrive(undefined)
    setSharePointDrive(
      (data?.project?.sharePointDrives ?? []).find(
        ({ id }) => id === data.project.projectMetadata?.customDriveId
      )
    )
  }, [
    data?.project?.projectMetadata?.customDriveId,
    data?.project?.sharePointDrives,
  ])
  return (
    <Provider
      value={{
        metadata: {
          ...data?.project?.projectMetadata,
          admins: data?.project?.projectToCIMSOnlineAdmins ?? [],
        },
        refetch,
        fetching,
        canEdit,
        editMetadata,
        projectLayer,
        projectLayerOptions,
        sharePointDrives,
        sharePointDrive,
        projectUserOptions: useOptions({
          items: projectAdminsQuery.data?.project?.users,
          value: 'id',
          label: 'username',
          sort: 'username',
          tooltip: ({ username, mail }) =>
            username === mail ? undefined : mail,
          disabled: ({ developer }) => developer,
        }),
        concreteTestIFISettings: {
          data: concreteTestData?.concreteTestIFISettings?.records[0],
          refetch: concreteTestRefetch,
          fetching: concreteTestFetching,
        },
        punchListIFISettings: {
          data: punchListData?.dailyInspectionPunchListIFISettings?.records[0],
          refetch: punchListRefetch,
          fetching: punchListFetching,
        },
        inspectionIFISettings: {
          data: inspectionData?.dailyInspectionInspectionIFISettings
            ?.records[0],
          refetch: inspectionRefetch,
          fetching: inspectionFetching,
        },
        controlPointIFISettings: {
          data: controlPointData?.dailyInspectionControlPointIFISettings
            ?.records[0],
          refetch: controlPointRefetch,
          fetching: controlPointFetching,
        },
        densityTestIFISettings: {
          data: densityTestData?.densityTestIFISettings?.records[0],
          refetch: densityTestRefetch,
          fetching: densityTestFetching,
        },
        lotIFISettings: {
          data: lotData?.lotIFISettings?.records[0],
          refetch: lotRefetch,
          fetching: lotFetching,
        },
      }}
    >
      {children}
    </Provider>
  )
}
